import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Package, MapPin, ShoppingBag } from 'lucide-react';
import { Page } from '../App';
const stats = [{
  id: 1,
  label: 'Orders Delivered',
  value: 2400000,
  suffix: '+',
  icon: Package,
  color: 'text-[#00FF87]'
}, {
  id: 2,
  label: 'States Covered',
  value: 36,
  suffix: '',
  icon: MapPin,
  color: 'text-[#D4AF37]'
}, {
  id: 3,
  label: 'Products Available',
  value: 50000,
  suffix: '+',
  icon: ShoppingBag,
  color: 'text-[#00FF87]'
}];
function Counter({
  value,
  duration = 2000
}: {
  value: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number | null = null;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * value));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  return <>
      {new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(count)}
    </>;
}
interface HeroSectionProps {
  onNavigate: (page: Page) => void;
}
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-101">
      <div className="absolute inset-0 z-0" data-id="element-102">
        <div className="absolute inset-0 bg-[#0F0F0F]" data-id="element-103" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} data-id="element-104" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37] rounded-full mix-blend-screen filter blur-[120px] opacity-[0.15] animate-pulse" data-id="element-105" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00FF87] rounded-full mix-blend-screen filter blur-[100px] opacity-[0.1]" data-id="element-106" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-107">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-108">
          <div className="text-center lg:text-left" data-id="element-109">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-110">
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] font-medium mb-4" data-id="element-111">
                Luxe Collections
              </p>
              <h1 className="text-5xl md:text-7xl font-bold font-serif text-white leading-tight mb-6" data-id="element-112">
                Delivered Across <br data-id="element-113" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F2D06B]" data-id="element-114">
                  Nigeria
                </span>
              </h1>
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: 'easeOut'
          }} className="text-lg md:text-xl text-gray-400 mb-8 max-w-lg mx-auto lg:mx-0 font-light" data-id="element-115">
              Premium accessories, home appliances & electronics. Trusted by
              Nigerians for quality and reliability.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-116">
              <button onClick={() => onNavigate('fashion')} className="px-8 py-4 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#c4a030] transition-all transform hover:scale-105 flex items-center gap-2" data-id="element-117">
                Shop Now <ArrowRight className="w-5 h-5" data-id="element-118" />
              </button>
              <button onClick={() => onNavigate('track-order')} className="px-8 py-4 border border-[#D4AF37] text-[#D4AF37] font-medium rounded-full hover:bg-[#D4AF37]/10 transition-all" data-id="element-119">
                Track Order
              </button>
            </motion.div>
          </div>

          <div className="mt-12 lg:mt-0" data-id="element-120">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-121">
              {stats.map((stat, index) => <motion.div key={stat.id} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.1
            }} className={`glass-panel p-6 rounded-xl border-l-4 ${index === 2 ? 'sm:col-span-2' : ''}`} style={{
              borderLeftColor: stat.color === 'text-[#D4AF37]' ? '#D4AF37' : '#00FF87'
            }} data-id="element-122">
                  <div className="flex items-center gap-4 mb-2" data-id="element-123">
                    <div className={`p-2 rounded-lg bg-white/5 ${stat.color}`} data-id="element-124">
                      <stat.icon className="w-6 h-6" data-id="element-125" />
                    </div>
                    <span className="text-gray-400 text-sm uppercase tracking-wider font-medium" data-id="element-126">
                      {stat.label}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-white font-serif" data-id="element-127">
                    <Counter value={stat.value} data-id="element-128" />
                    {stat.suffix}
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1,
      duration: 1
    }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2" data-id="element-129">
        <span className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-130">
          Scroll
        </span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-[#D4AF37] to-transparent" data-id="element-131" />
      </motion.div>
    </section>;
}